/**
 * Sugestiile de pozitie pe plan (AP-uri din survey, aparate din amprenta
 * Wi-Fi), ca functii pure: ce se arata, ce e deja pus, ce inseamna Accept.
 *
 * Socoteala pozitiilor e pe server (`Floors/Placement.php`); aici se hotaraste
 * numai ce vede omul, fiindca planul se schimba in browser (undo, autosave):
 *  - o sugestie `ok`, nepusa si nerefuzata -> marcaj "suggested" pe plan;
 *  - un AP / aparat deja pus -> nimic pe plan, dar daca sugestia e la mai mult
 *    de `FAR_M` metri de el, un rand discret "survey suggests X m away";
 *  - Accept -> item nou la pozitia sugerata (sau, pentru unul deja pus,
 *    mutat acolo).
 */
import type { FloorAp, FloorItem, SuggestionsPage } from '../api/floorPlans'
import { apOf } from './viewModel'

/** Textul de pe plan care explica sugestiile (si ca sunt o estimare). */
export const SUGGEST_EXPLAIN =
  'Suggested from the survey: access points sit where their signal was strongest; devices are placed where their Wi-Fi fingerprint matches the survey. Accept or move them.'

/** Peste atata distanta intre ce a pus omul si sugestie, se spune. */
export const FAR_M = 5

export type Suggestion = {
  /** `ap:<cheie>` / `device:<id>` - si id-ul marcajului pe harta. */
  id: string
  kind: 'ap' | 'device'
  key: string | null
  device_id: number | null
  spot_id: number | null
  label: string
  x_cm: number
  y_cm: number
  radius_cm: number
  detail: string
  /** Indexul item-ului deja pus pe plan, daca e. */
  placedIndex: number | null
  /** Cat de departe e sugestia de item-ul pus, in metri. */
  offsetM: number | null
}

export type PlanSuggestions = {
  /** De aratat pe plan, cu Accept / Dismiss. */
  pending: Suggestion[]
  /** Deja puse, dar survey-ul le vede la peste FAR_M metri. */
  far: Suggestion[]
  /** AP-uri auzite, dar fara destule date. */
  apsWithoutData: number
}

function sameAp(item: FloorItem, key: string, aps: FloorAp[]): boolean {
  if (item.kind !== 'ap' || item.ap_group_key === null) return false
  if (item.ap_group_key === key) return true
  const ap = apOf(item.ap_group_key, aps)
  return ap !== undefined && (ap.key === key || ap.keys.includes(key))
}

export function planSuggestions(page: SuggestionsPage | undefined, items: FloorItem[], aps: FloorAp[]): PlanSuggestions {
  const out: PlanSuggestions = { pending: [], far: [], apsWithoutData: 0 }
  if (!page) return out
  const dismissedAps = new Set((page.dismissed?.aps ?? []).map((k) => k.toLowerCase()))
  const dismissedDevices = page.dismissed?.devices ?? {}

  const add = (s: Omit<Suggestion, 'placedIndex' | 'offsetM'>, placedIndex: number) => {
    if (placedIndex < 0) {
      out.pending.push({ ...s, placedIndex: null, offsetM: null })
      return
    }
    const it = items[placedIndex]
    const offsetM = Math.hypot(it.x_cm - s.x_cm, it.y_cm - s.y_cm) / 100
    if (offsetM > FAR_M) out.far.push({ ...s, placedIndex, offsetM })
  }

  for (const a of page.aps ?? []) {
    if (a.status !== 'ok') {
      out.apsWithoutData++
      continue
    }
    const placed = items.findIndex((i) => sameAp(i, a.key, aps))
    if (dismissedAps.has(a.key.toLowerCase())) continue
    add(
      {
        id: `ap:${a.key}`,
        kind: 'ap',
        key: a.key,
        device_id: null,
        spot_id: null,
        label: a.name ?? a.key,
        x_cm: a.x_cm,
        y_cm: a.y_cm,
        radius_cm: a.radius_cm,
        detail: `Access point - strongest at ${Math.round(a.best_rssi)} dBm, heard at ${a.spots} spots`,
      },
      placed,
    )
  }

  for (const d of page.devices ?? []) {
    if (d.status !== 'ok' || d.x_cm === undefined || d.y_cm === undefined) continue
    const placed = items.findIndex((i) => i.kind === 'device' && i.device_id === d.device_id)
    if (dismissedDevices[String(d.device_id)] === d.spot_id) continue
    add(
      {
        id: `device:${d.device_id}`,
        kind: 'device',
        key: null,
        device_id: d.device_id,
        spot_id: d.spot_id,
        label: d.name,
        x_cm: d.x_cm,
        y_cm: d.y_cm,
        radius_cm: d.radius_cm ?? 200,
        detail: `Device - Wi-Fi fingerprint, ${d.common_aps ?? 0} access points in common, ${d.confidence ?? 'low'} confidence`,
      },
      placed,
    )
  }
  return out
}

/** Accept: item nou la pozitia sugerata, sau cel deja pus mutat acolo. */
export function acceptSuggestion(items: FloorItem[], s: Suggestion): FloorItem[] {
  if (s.placedIndex !== null && items[s.placedIndex]) {
    return items.map((it, i) => (i === s.placedIndex ? { ...it, x_cm: s.x_cm, y_cm: s.y_cm } : it))
  }
  const item: FloorItem =
    s.kind === 'ap'
      ? { kind: 'ap', ap_group_key: s.key, device_id: null, label: null, x_cm: s.x_cm, y_cm: s.y_cm, attrs: null }
      : { kind: 'device', ap_group_key: null, device_id: s.device_id, label: null, x_cm: s.x_cm, y_cm: s.y_cm, attrs: null }
  return [...items, item]
}

/** Accept all: toate sugestiile nepuse, intr-un singur pas (un singur undo). */
export function acceptAll(items: FloorItem[], pending: Suggestion[]): FloorItem[] {
  return pending.filter((s) => s.placedIndex === null).reduce((acc, s) => acceptSuggestion(acc, s), items)
}

/** Ce spune randul unui aparat localizat care n-are (inca) pozitie. */
export function locateStatus(d: SuggestionsPage['devices'][number]): string | null {
  switch (d.status) {
    case 'pending':
      return 'Measuring...'
    case 'timeout':
      return 'The device did not answer. Check that it is online.'
    case 'error':
      return d.error ?? 'The measurement failed.'
    case 'not_enough_data':
      return 'Not enough data: it hears fewer than 2 of the access points measured in the survey.'
    default:
      return null
  }
}
